import { useMatchBreakpoints } from '@pancakeswap/uikit'
import Link from 'next/link'
import styled from 'styled-components'
import { fmtAge } from '../format'
import { useChart } from '../hooks'
import { TIMEFRAMES } from '../config'
import { GhostCta, Panel, Segmented, SegBtn, Skel, useNow } from '../ui'
import PoolPicker from './PoolPicker'
import PriceChart, { ChartMark } from './PriceChart'
import WolfState from './WolfState'
import type { TfKey, TokenOverview } from '../types'

const Bar = styled.div`
  display: grid;
  gap: 8px;
  margin-bottom: 12px;
  /* timeframes and pool side by side only when the chart column is wide enough for six 44 px buttons next to the pool list */
  ${({ theme }) => theme.mediaQueries.xl} {
    grid-template-columns: minmax(0, 1fr) minmax(0, 280px);
    align-items: center;
  }
`
const Foot = styled.div`
  margin-top: 8px;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.textSubtle};
  a {
    color: inherit;
    text-decoration: underline;
  }
`

/** The price chart of one pool of a token: timeframes, the pool picker (unless the pool is fixed), candles, honest empty and busy states, optional start and end marks. */
export default function ChartCard({
  o,
  pool,
  onPool,
  tf,
  onTf,
  marks,
  hidePicker,
}: {
  o: TokenOverview
  pool: string | null
  onPool: (p: string) => void
  tf: TfKey
  onTf: (t: TfKey) => void
  /** a competition's start and end, drawn on the candles that contain them (memoise the array) */
  marks?: ChartMark[]
  /** the pool is not the viewer's to choose (a competition's pool) */
  hidePicker?: boolean
}) {
  const { isMobile } = useMatchBreakpoints()
  const { data, error, isLoading } = useChart(o.address, pool, tf)
  const sel = o.pools.find((p) => p.pool.toLowerCase() === pool?.toLowerCase())
  const now = useNow(60_000)
  // keepPreviousData shows the last answer while the next one loads: never call that an empty chart, it is the wrong pool or timeframe
  const fresh = Boolean(data) && data?.tf === tf && data?.pool.toLowerCase() === pool?.toLowerCase()
  const empty = fresh && data?.candles.length === 0
  const height = isMobile ? 280 : 340
  return (
    <Panel className="chart" data-testid="tp-chart-card">
      <Bar>
        <Segmented $cols={TIMEFRAMES.length} role="tablist" aria-label="Chart timeframe">
          {TIMEFRAMES.map((t) => (
            <SegBtn
              key={t.key}
              role="tab"
              aria-selected={tf === t.key}
              $on={tf === t.key}
              onClick={() => onTf(t.key)}
              data-testid={`tp-tf-${t.key}`}
              style={{ padding: 0 }}
            >
              {t.label}
            </SegBtn>
          ))}
        </Segmented>
        {o.pools.length > 0 && !hidePicker && <PoolPicker pools={o.pools} value={pool} onChange={onPool} label="Pool on the chart" testId="tp-pool-select" />}
      </Bar>
      <div style={{ position: 'relative', minHeight: height }}>
        {((isLoading && !data) || (Boolean(data) && !fresh && !error)) && (
          <Skel $h={height} $r={16} aria-busy="true" data-testid="tp-chart-loading" />
        )}
        {error && !data && (
          <WolfState
            testId="tp-chart-error"
            title={error.message === '503' ? 'The price source is busy' : 'The chart could not be loaded'}
            body={
              error.message === '503'
                ? 'Charts refresh by themselves. Give it a moment.'
                : 'The price history for this pool is not available right now. Try another pool or timeframe.'
            }
          />
        )}
        {o.pools.length === 0 && (
          <WolfState
            testId="tp-no-pool"
            title="No verified pool yet"
            body={
              o.major
                ? `${o.symbol} is a major asset: trade it in Swap.`
                : `${
                    o.symbol ?? 'This token'
                  } has no pool on the supported exchanges, so there is no price history to draw.`
            }
            actions={
              !o.major ? (
                <Link href={`/add/${o.address}`} passHref legacyBehavior>
                  <GhostCta style={{ minHeight: 48 }}>Add liquidity</GhostCta>
                </Link>
              ) : undefined
            }
          />
        )}
        {data && fresh && !empty && (
          <PriceChart candles={data.candles} height={height} unit={data.unit} quoteSymbol={data.quoteSymbol} marks={marks} />
        )}
        {empty && (
          <WolfState
            testId="tp-chart-empty"
            title="No trades in this period"
            body={
              sel?.lastTradeAt && now
                ? `Last trade in this pool: ${fmtAge(sel.lastTradeAt, now).replace(
                    ' old',
                    ' ago',
                  )}. Try a longer timeframe.`
                : 'Nothing has traded in this pool during this period. Try a longer timeframe.'
            }
          />
        )}
      </div>
      <Foot>
        {data?.source === 'gecko' && 'Prices in USD from GeckoTerminal. '}
        {(data?.source === 'cadinudex' || data?.source === 'subgraph') && `${data.note ?? 'From CADINUdex swaps.'} `}
        Candles drawn with{' '}
        <a href="https://www.tradingview.com/" target="_blank" rel="noopener noreferrer">
          TradingView Lightweight Charts
        </a>
        .
      </Foot>
    </Panel>
  )
}
